import * as signalR from '@microsoft/signalr'

export type AppNotificationPayload = {
  notificationId: number
  notificationTypeId: number
  notificationType: string
  userAccountId: number
  expenseId: number
  trackingId: string | null
  expenseTitle: string | null
  expenseStatusId: number
  message: string
  createdDateUtc: string | null
}

type AppNotificationHandler = (payload: AppNotificationPayload) => void

let connection: signalR.HubConnection | null = null
let handlers: AppNotificationHandler[] = []

const HUB_PATH = '/SS_AppNotifications'
const RECEIVE_EVENT = 'ReceiveAppNotification'

export const subscribeAppNotifications = (handler: AppNotificationHandler) => {
  handlers.push(handler)
  return () => {
    handlers = handlers.filter((h) => h !== handler)
  }
}

const notify = (payload: AppNotificationPayload) => {
  handlers.forEach((h) => {
    try {
      h(payload)
    } catch {
      /* ignore handler errors */
    }
  })
}

const normalize = (raw: any): AppNotificationPayload | null => {
  const userAccountId = Number(raw?.userAccountId ?? raw?.UserAccountId ?? 0)
  const expenseId = Number(raw?.expenseId ?? raw?.ExpenseId ?? 0)
  if (!userAccountId && !expenseId) return null
  return {
    notificationId: Number(raw?.notificationId ?? raw?.NotificationId ?? 0),
    notificationTypeId: Number(raw?.notificationTypeId ?? raw?.NotificationTypeId ?? 0),
    notificationType: String(raw?.notificationType ?? raw?.NotificationType ?? 'Notification'),
    userAccountId,
    expenseId,
    trackingId: raw?.trackingId ?? raw?.TrackingId ?? null,
    expenseTitle: raw?.expenseTitle ?? raw?.ExpenseTitle ?? null,
    expenseStatusId: Number(raw?.expenseStatusId ?? raw?.ExpenseStatusId ?? 0),
    message: String(raw?.message ?? raw?.Message ?? ''),
    createdDateUtc: raw?.createdDateUtc ?? raw?.CreatedDateUtc ?? null,
  }
}

const buildHubUrl = (baseURL: string, userAccountId?: number | null) => {
  const params = new URLSearchParams()
  if (userAccountId) {
    params.set('orgUserId', String(userAccountId))
    params.set('userAccountId', String(userAccountId))
  }
  const qs = params.toString()
  const root = `${baseURL.replace(/\/$/, '')}${HUB_PATH}`
  return qs ? `${root}?${qs}` : root
}

export const startSSAppNotificationsHub = async (opts: {
  baseURL?: string | null
  token?: string | null
  userAccountId?: number | null
}) => {
  const { baseURL, token, userAccountId } = opts
  if (!baseURL || !token) return null

  if (connection) {
    if (connection.state === signalR.HubConnectionState.Connected) return connection
    try {
      await connection.stop()
    } catch {
      /* ignore */
    }
    connection = null
  }

  connection = new signalR.HubConnectionBuilder()
    .withUrl(buildHubUrl(baseURL, userAccountId), {
      accessTokenFactory: () => token,
      transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Warning)
    .build()

  connection.on(RECEIVE_EVENT, (payload: any) => {
    const normalized = normalize(payload)
    if (normalized) notify(normalized)
  })

  try {
    await connection.start()
  } catch (err) {
    console.warn('SS_AppNotifications connect failed', err)
    connection = null
    return null
  }

  return connection
}

export const stopSSAppNotificationsHub = async () => {
  if (!connection) return
  try {
    await connection.stop()
  } catch {
    /* ignore */
  }
  connection = null
}
